import { useMemo } from 'react';

import { Sphere } from '@react-three/drei';
import { useAtomValue } from 'jotai';

import { PositionNumber } from '@core';
import {
    activeData3dAtom,
    activeUserSettingsAtom,
    comparisonData3dAtom,
    showVisualizationsAtom,
    traceAtom,
    useEnsureAtomValue,
} from '@store';
import { colors } from '@ui';

import { ComparisonMirroring } from './ComparisonMirroring';
import { GlobalRotation } from './GlobalRotation';
import { VISUALIZATION_AXES, VISUALIZATION_MEASUREMENTS } from './mappings';
import { Club } from './Skeleton/ClubTrack';
import { Skeleton } from './Skeleton/Skeleton';
import { type FocusedMeasurement } from './types';
import { useAngleDirections } from './useAngles';
import { useBallOrigin } from './useBallOrigin';
import { MemoAngleVisualization } from './visualizations/AngleVisualization';
import { MemoDisplacementVisualization } from './visualizations/DisplacementVisualization';
import { MemoKeypointPath } from './visualizations/KeypointPath';
import { MemoPaths } from './visualizations/Paths';
import { MemoSwingPlanes } from './visualizations/SwingPlanes';

// Golf ball diameter is 42.7mm, radius is 21.35mm
export const GOLF_BALL_RADIUS = 0.02135;

type SceneCompositionProps = {
    currentFrame: number;
    currentFocus: FocusedMeasurement;
    currentTrace?: string | null;
    freezePosition: string | null;
    comparisonFrame?: number;
    overlayComparison?: boolean;
};

// Side-by-side mode: offset -1m on lateral (Y) axis in golf coords
const sideOffset: [number, number, number] = [0, -1, 0];
const overlayOffset: [number, number, number] = [0, 0, 0];

export function SceneComposition({
    currentFrame,
    currentFocus,
    freezePosition,
    comparisonFrame,
    overlayComparison = false,
}: SceneCompositionProps) {
    const activeData = useEnsureAtomValue(activeData3dAtom);
    const comparisonData = useAtomValue(comparisonData3dAtom);
    const userSettings = useAtomValue(activeUserSettingsAtom);
    const showVisualizations = useAtomValue(showVisualizationsAtom);

    const originOffset = useBallOrigin(activeData?.golfBallTranslation);
    const comparisonOriginOffset = useBallOrigin(comparisonData?.golfBallTranslation);

    const directions = useAngleDirections(currentFocus, activeData, currentFrame);
    const comparisonDirections = useAngleDirections(currentFocus, comparisonData, currentFrame);

    const freezeFrame = useMemo(() => {
        const position = freezePosition as `P${PositionNumber}`;
        if (activeData.segmentation[position]) {
            return activeData.segmentation[position];
        }
        return null;
    }, [activeData.segmentation, freezePosition]);

    const targetPoints = currentFocus?.axisData.targetPoints ?? [];

    const traceIds = useAtomValue(traceAtom);
    const currentTraces = useMemo(() => {
        return traceIds
            .map((id) => {
                const measurement = VISUALIZATION_MEASUREMENTS[id];
                if (!measurement) return null;
                const axisId = measurement.axes[0];
                if (!axisId) return null;
                const targetPoint = VISUALIZATION_AXES[axisId]?.targetPoints?.[0];
                return targetPoint;
            })
            .filter((keypointName): keypointName is string => keypointName !== null);
    }, [traceIds]);

    return (
        <GlobalRotation>
            <group position={originOffset}>
                <Sphere args={[GOLF_BALL_RADIUS]} position={activeData?.golfBallTranslation}>
                    <meshStandardMaterial color={colors.slate[400]} />
                </Sphere>
                <Skeleton
                    frame={currentFrame}
                    {...activeData}
                    targetPoints={targetPoints}
                    comparisonFrame={freezeFrame}
                    measurements={activeData.measurements}
                />
                <Club frame={currentFrame} clubTrack={activeData.clubTrack} comparisonFrame={freezeFrame} />

                {showVisualizations &&
                    currentTraces.map((trace) => (
                        <MemoKeypointPath
                            frame={currentFrame}
                            skeleton={activeData.skeleton}
                            keypointIdToName={activeData.keypointIdToName}
                            segmentation={activeData.segmentation}
                            keypointName={trace}
                        />
                    ))}

                {showVisualizations && currentFocus?.type === 'displacement' && (
                    <MemoDisplacementVisualization
                        frame={currentFrame}
                        referenceFrame={activeData.segmentation['P1']}
                        targetPoints={currentFocus?.axisData.targetPoints ?? []}
                        skeleton={activeData.skeleton}
                        keypointIdToName={activeData.keypointIdToName}
                        currentFocus={currentFocus}
                    />
                )}
                {showVisualizations && currentFocus?.type === 'distance' && (
                    <MemoDisplacementVisualization
                        frame={currentFrame}
                        referenceFrame={activeData.segmentation['P1']}
                        targetPoints={currentFocus?.axisData.targetPoints ?? []}
                        skeleton={activeData.skeleton}
                        keypointIdToName={activeData.keypointIdToName}
                        currentFocus={currentFocus}
                    />
                )}
                {showVisualizations && currentFocus?.type === 'angle' && (
                    <MemoAngleVisualization
                        type={currentFocus.measurementType}
                        frame={currentFrame}
                        skeleton={activeData.skeleton}
                        targetPoints={currentFocus?.axisData.targetPoints ?? []}
                        keypointIdToName={activeData.keypointIdToName}
                        currentFocus={currentFocus}
                        referenceDirection={directions?.referenceDirection}
                        actualDirection={directions?.actualDirection}
                    />
                )}
                {showVisualizations && currentFocus?.type === 'jointAngle' && (
                    <MemoAngleVisualization
                        type={currentFocus.measurementType}
                        frame={currentFrame}
                        skeleton={activeData.skeleton}
                        targetPoints={currentFocus?.axisData.targetPoints ?? []}
                        keypointIdToName={activeData.keypointIdToName}
                        currentFocus={currentFocus}
                    />
                )}
                {showVisualizations && <MemoSwingPlanes swing="active" />}

                {showVisualizations && (
                    <MemoPaths
                        frame={currentFrame}
                        skeleton={activeData.skeleton}
                        keypointIdToName={activeData.keypointIdToName}
                        segmentation={activeData.segmentation}
                        clubTrack={activeData.clubTrack}
                    />
                )}
            </group>
            {userSettings.showComparison && comparisonData && typeof comparisonFrame === 'number' && (
                <group position={overlayComparison ? overlayOffset : sideOffset}>
                    <ComparisonMirroring>
                        <group position={comparisonOriginOffset}>
                            <Sphere args={[GOLF_BALL_RADIUS]} position={comparisonData.golfBallTranslation}>
                                <meshStandardMaterial color={colors.slate[400]} transparent opacity={0.5} />
                            </Sphere>
                            <Skeleton frame={comparisonFrame} {...comparisonData} comparisonFrame={null} isComparison />
                            <Club
                                frame={comparisonFrame}
                                clubTrack={comparisonData.clubTrack}
                                comparisonFrame={null}
                                isComparison
                            />
                            {showVisualizations && currentFocus?.type === 'angle' && (
                                <MemoAngleVisualization
                                    type={currentFocus.measurementType}
                                    frame={comparisonFrame}
                                    skeleton={comparisonData.skeleton}
                                    targetPoints={currentFocus?.axisData.targetPoints ?? []}
                                    keypointIdToName={comparisonData.keypointIdToName}
                                    currentFocus={currentFocus}
                                    opacity={0.5}
                                    showFill={false}
                                    referenceDirection={comparisonDirections?.referenceDirection}
                                    actualDirection={comparisonDirections?.actualDirection}
                                />
                            )}
                            {showVisualizations && currentFocus?.type === 'jointAngle' && (
                                <MemoAngleVisualization
                                    type={currentFocus.measurementType}
                                    frame={comparisonFrame}
                                    skeleton={comparisonData.skeleton}
                                    targetPoints={currentFocus?.axisData.targetPoints ?? []}
                                    keypointIdToName={comparisonData.keypointIdToName}
                                    currentFocus={currentFocus}
                                    opacity={0.5}
                                    showFill={false}
                                />
                            )}
                            {showVisualizations && <MemoSwingPlanes swing="comparison" />}
                            {showVisualizations && (
                                <MemoPaths
                                    frame={comparisonFrame}
                                    skeleton={comparisonData.skeleton}
                                    keypointIdToName={comparisonData.keypointIdToName}
                                    segmentation={comparisonData.segmentation}
                                    clubTrack={comparisonData.clubTrack}
                                    opacity={0.5}
                                />
                            )}
                        </group>
                    </ComparisonMirroring>
                </group>
            )}
        </GlobalRotation>
    );
}
